import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  ChevronRight,
  PhoneCall,
  Plus,
  Trash2,
  UserRound,
  Users,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { CountryFlag } from "../components/CountryFlag";
import { formatPhoneNumber } from "../lib/phone";
import {
  numbersApi,
  SCRIPT_TYPE_IDS,
  SCRIPT_TYPE_OPTIONS,
  isKnownScriptType,
  type NumberDestinationItem,
  type NumberDetail,
  type NumberHuntGroupItem,
  type NumberScheduleItem,
  type ScriptTypeId,
} from "../api/numbersApi";
import {
  applicationsApi,
  type ApplicationListItem,
} from "../api/applicationsApi";
import { agentsApi } from "../api/agentsApi";
import { huntgroupsApi } from "../api/huntgroupsApi";
import { HttpError } from "../api/http";
import { AgentStatusChip } from "../components/AgentStatusChip";
import {
  ScheduleStateChip,
  formatDateTime,
} from "../components/schedule/ScheduleChips";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { Modal } from "../components/ui/Modal";

type LoadState =
  | { status: "loading" }
  | {
      status: "ready";
      detail: NumberDetail;
      schedule: NumberScheduleItem[];
      /**
       * Agents whose name starts with this dnis's digits-only
       * presentation. Only fetched when scriptType=Destination, otherwise
       * <code>null</code> (we don't render the tile at all). Empty
       * <code>items</code> means there is no linked destination yet.
       */
      destinations: {
        prefix: string;
        items: NumberDestinationItem[];
      } | null;
      /**
       * Huntgroups whose name starts with this dnis's digits-only
       * presentation. Only fetched when scriptType=Huntgroup, otherwise
       * <code>null</code>. Empty <code>items</code> means there is no
       * linked huntgroup yet.
       */
      huntgroups: {
        prefix: string;
        items: NumberHuntGroupItem[];
      } | null;
    }
  | { status: "error"; message: string };

/**
 * Per-number detail page. Shows everything the customer wants to know
 * about a single DNIS — country/area metadata, validity window, tariff
 * and the current+future application schedule — and lets them schedule
 * a new assignment or remove a pending one. Mirrors the legacy
 * NumberEdit Razor view (Schedule App modal + table) but additionally
 * surfaces context that previously lived in the Numbers overview only.
 */
export function NumberEditPage() {
  const { dnisId: dnisParam } = useParams<{ dnisId: string }>();
  const dnisId = Number(dnisParam);
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleRemovePending, setScheduleRemovePending] = useState<
    number | null
  >(null);
  const [prefixedCreate, setPrefixedCreate] = useState<{
    kind: "destination" | "huntgroup";
    prefix: string;
  } | null>(null);
  const [prefixedName, setPrefixedName] = useState("");
  const [prefixedError, setPrefixedError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!Number.isFinite(dnisId) || dnisId <= 0) {
      setState({ status: "error", message: "Invalid number id." });
      return;
    }
    try {
      const [detail, schedule] = await Promise.all([
        numbersApi.get(dnisId),
        numbersApi.getSchedule(dnisId),
      ]);
      // Destinations / huntgroups tiles are mutually exclusive based on
      // scriptType. Skipping the call for the other type saves a
      // round-trip and keeps the surface tidy. If the call fails we
      // still want the tile to let the user add a destination/huntgroup,
      // so we fall back to the dnis-string itself (already digits-only).
      const destinations =
        detail.scriptTypeId === SCRIPT_TYPE_IDS.destination
          ? await numbersApi
              .getDestinations(dnisId)
              .then((r) => ({
                prefix: r.prefix || detail.dnisRaw || "",
                items: r.items ?? [],
              }))
              .catch(() => ({ prefix: detail.dnisRaw || "", items: [] }))
          : null;
      const huntgroups =
        detail.scriptTypeId === SCRIPT_TYPE_IDS.huntgroup
          ? await numbersApi
              .getHuntGroups(dnisId)
              .then((r) => ({
                prefix: r.prefix || detail.dnisRaw || "",
                items: r.items ?? [],
              }))
              .catch(() => ({ prefix: detail.dnisRaw || "", items: [] }))
          : null;
      setState({
        status: "ready",
        detail,
        schedule: schedule.items ?? [],
        destinations,
        huntgroups,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load number";
      setState({ status: "error", message });
    }
  }, [dnisId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!prefixedCreate) {
      setPrefixedName("");
      setPrefixedError(null);
      return;
    }
    const suggestion =
      prefixedCreate.kind === "destination"
        ? `${prefixedCreate.prefix} - Destination`
        : `${prefixedCreate.prefix} - Huntgroup`;
    setPrefixedName(suggestion);
    setPrefixedError(null);
  }, [prefixedCreate]);

  const runRemoveSchedule = useCallback(
    async (applicationDnisId: number) => {
      setError(null);
      setBusy(true);
      try {
        const next = await numbersApi.deleteSchedule(
          dnisId,
          applicationDnisId,
        );
        setState((prev) =>
          prev.status === "ready"
            ? { ...prev, schedule: next.items ?? [] }
            : prev,
        );
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to delete schedule",
        );
      } finally {
        setBusy(false);
      }
    },
    [dnisId],
  );

  const requestRemoveSchedule = useCallback((applicationDnisId: number) => {
    setScheduleRemovePending(applicationDnisId);
  }, []);

  const openCreateDestination = useCallback((prefix: string) => {
    if (!prefix) return;
    setPrefixedCreate({ kind: "destination", prefix });
  }, []);

  const openCreateHuntGroup = useCallback((prefix: string) => {
    if (!prefix) return;
    setPrefixedCreate({ kind: "huntgroup", prefix });
  }, []);

  const submitPrefixedCreate = useCallback(async () => {
    if (!prefixedCreate) return;
    const trimmed = prefixedName.trim();
    if (!trimmed) {
      setPrefixedError("Name is required.");
      return;
    }
    if (!trimmed.startsWith(prefixedCreate.prefix)) {
      setPrefixedError(
        `The name must start with the dnis prefix "${prefixedCreate.prefix}".`,
      );
      return;
    }
    setPrefixedError(null);
    setBusy(true);
    try {
      if (prefixedCreate.kind === "destination") {
        const created = await agentsApi.create(trimmed);
        setPrefixedCreate(null);
        navigate(`/destinations/${created.agentId}`);
      } else {
        const created = await huntgroupsApi.create({
          hgName: trimmed,
          hgRef: "",
          description: "",
          queueDistributionTypeId: 1,
          queueLimitTypeId: 1,
          queueLimit: 0,
          postCallHandle: 0,
          recordCalls: false,
        });
        setPrefixedCreate(null);
        navigate(`/huntgroups/${created.huntGroupId}`);
      }
    } catch (err) {
      const message =
        err instanceof HttpError
          ? `Failed to create ${
              prefixedCreate.kind === "destination"
                ? "destination"
                : "huntgroup"
            } (${err.status}).`
          : err instanceof Error
            ? err.message
            : "Failed to create";
      setPrefixedError(message);
    } finally {
      setBusy(false);
    }
  }, [prefixedCreate, prefixedName, navigate]);

  const onChangeScriptType = useCallback(
    async (scriptTypeId: ScriptTypeId) => {
      setError(null);
      setBusy(true);
      try {
        const detail = await numbersApi.updateScriptType(dnisId, scriptTypeId);
        // Re-fetch the schedule too: switching away from FlowBuilder
        // hides it, switching back surfaces it. We could in principle
        // keep the cached schedule when scriptTypeId stays = 2, but a
        // single round-trip keeps the rendering rules dead simple.
        const schedule = await numbersApi.getSchedule(dnisId);
        const destinations =
          scriptTypeId === SCRIPT_TYPE_IDS.destination
            ? await numbersApi
                .getDestinations(dnisId)
                .then((r) => ({
                  prefix: r.prefix || detail.dnisRaw || "",
                  items: r.items ?? [],
                }))
                .catch(() => ({ prefix: detail.dnisRaw || "", items: [] }))
            : null;
        const huntgroups =
          scriptTypeId === SCRIPT_TYPE_IDS.huntgroup
            ? await numbersApi
                .getHuntGroups(dnisId)
                .then((r) => ({
                  prefix: r.prefix || detail.dnisRaw || "",
                  items: r.items ?? [],
                }))
                .catch(() => ({ prefix: detail.dnisRaw || "", items: [] }))
            : null;
        setState({
          status: "ready",
          detail,
          schedule: schedule.items ?? [],
          destinations,
          huntgroups,
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to change application",
        );
      } finally {
        setBusy(false);
      }
    },
    [dnisId],
  );

  const onAddScheduled = async (
    applicationId: number,
    validFromUtc: string | null,
  ) => {
    setError(null);
    setBusy(true);
    try {
      const next = await numbersApi.addSchedule(dnisId, {
        applicationId,
        validFromUtc,
      });
      setState((prev) =>
        prev.status === "ready"
          ? { ...prev, schedule: next.items ?? [] }
          : prev,
      );
      setShowAdd(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add schedule");
    } finally {
      setBusy(false);
    }
  };

  const title =
    state.status === "ready"
      ? formatPhoneNumber(
          state.detail.number,
          state.detail.countryCode,
          state.detail.areaCode,
        ) ||
        state.detail.number ||
        "Number details"
      : "Number details";

  return (
    <>
      <PageHeader
        title={title}
        actions={
          <Link
            to="/numbers"
            aria-label="Back to numbers"
            className="btn btn-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}
          >
            <ArrowLeft size={14} /> Back to numbers
          </Link>
        }
      />
      <div className="dashboard">
        {state.status === "loading" && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading number…
          </div>
        )}
        {state.status === "error" && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            <strong>Could not load this number.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>{state.message}</div>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 12 }}
              onClick={() => navigate("/numbers")}
            >
              Back to numbers
            </button>
          </div>
        )}
        {state.status === "ready" && (
          <>
            <NumberHero detail={state.detail} />
            <NumberQuickLinks detail={state.detail} />
            <NumberMetadataTile detail={state.detail} />

            <NumberApplicationTile
              detail={state.detail}
              busy={busy}
              error={error}
              onChange={onChangeScriptType}
            />

            {state.detail.scriptTypeId === SCRIPT_TYPE_IDS.destination &&
              state.destinations && (
                <NumberDestinationsTile
                  prefix={state.destinations.prefix}
                  items={state.destinations.items}
                  busy={busy}
                  onAdd={() =>
                    openCreateDestination(state.destinations?.prefix ?? "")
                  }
                  onOpen={(agentId) => navigate(`/destinations/${agentId}`)}
                />
              )}

            {state.detail.scriptTypeId === SCRIPT_TYPE_IDS.huntgroup &&
              state.huntgroups && (
                <NumberHuntGroupsTile
                  prefix={state.huntgroups.prefix}
                  items={state.huntgroups.items}
                  busy={busy}
                  onAdd={() =>
                    openCreateHuntGroup(state.huntgroups?.prefix ?? "")
                  }
                  onOpen={(huntGroupId) =>
                    navigate(`/huntgroups/${huntGroupId}`)
                  }
                />
              )}

            {state.detail.scriptTypeId === SCRIPT_TYPE_IDS.flowBuilder && (
              <section
                className="tile"
                aria-labelledby="schedule-heading"
                style={{ gap: 12 }}
              >
                <header
                  className="tile-head"
                  style={{ display: "flex", alignItems: "center", gap: 12 }}
                >
                  <h3 id="schedule-heading" style={{ margin: 0 }}>
                    Flow schedule
                  </h3>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowAdd(true)}
                    disabled={busy || !state.detail.allowEdit}
                    title={
                      state.detail.allowEdit
                        ? "Schedule a future flow"
                        : "This number cannot be re-scheduled"
                    }
                    style={{
                      marginLeft: "auto",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Plus size={16} /> Schedule flow
                  </button>
                </header>

                {error && (
                  <div
                    style={{
                      color: "#c0392b",
                      background: "#fdecea",
                      border: "1px solid #e74c3c",
                      borderRadius: 8,
                      padding: 10,
                      fontSize: "0.9rem",
                    }}
                  >
                    {error}
                  </div>
                )}

                {state.schedule.length === 0 ? (
                  <div style={{ color: "var(--portal-muted)" }}>
                    No flow assigned to this number yet.
                  </div>
                ) : (
                  <ScheduleTable
                    rows={state.schedule}
                    onDelete={requestRemoveSchedule}
                    busy={busy}
                  />
                )}
              </section>
            )}
          </>
        )}

        {showAdd && (
          <ScheduleAppModal
            onCancel={() => setShowAdd(false)}
            onConfirm={onAddScheduled}
            busy={busy}
          />
        )}
      </div>

      {state.status === "ready" && scheduleRemovePending != null && (
        <ConfirmModal
          open
          onClose={() => {
            if (!busy) setScheduleRemovePending(null);
          }}
          title="Remove scheduled assignment?"
          confirmLabel="Remove"
          tone="caution"
          busy={busy}
          onConfirm={() => {
            const id = scheduleRemovePending;
            setScheduleRemovePending(null);
            if (id != null) void runRemoveSchedule(id);
          }}
        >
          Remove the scheduled assignment for{" "}
          <strong>
            {state.schedule.find(
              (s) => s.applicationDnisId === scheduleRemovePending,
            )?.name ?? "this entry"}
          </strong>
          ?
        </ConfirmModal>
      )}

      <Modal
        open={prefixedCreate != null}
        onClose={() => {
          if (!busy) setPrefixedCreate(null);
        }}
        title={
          prefixedCreate?.kind === "huntgroup"
            ? "New huntgroup"
            : "New destination"
        }
        busy={busy}
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setPrefixedCreate(null)}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void submitPrefixedCreate()}
              disabled={busy || !prefixedName.trim()}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Plus size={16} />
              {busy ? "Creating…" : "Create"}
            </button>
          </>
        }
      >
        {prefixedCreate && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitPrefixedCreate();
            }}
            style={{ display: "grid", gap: 10 }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "0.88rem",
                color: "var(--portal-muted)",
              }}
            >
              The name must start with prefix{" "}
              <strong>{prefixedCreate.prefix}</strong> so routing matches this
              number.
            </p>
            <label style={{ display: "grid", gap: 4 }}>
              <span
                style={{
                  fontSize: "0.8rem",
                  color: "var(--portal-muted)",
                  fontWeight: 600,
                }}
              >
                Name
              </span>
              <input
                type="text"
                value={prefixedName}
                onChange={(e) => setPrefixedName(e.target.value)}
                disabled={busy}
                maxLength={50}
                autoFocus
                style={{
                  padding: "8px 12px",
                  border: "1px solid var(--portal-border)",
                  borderRadius: 6,
                  fontSize: "0.95rem",
                  fontFamily: "inherit",
                  background: "#ffffff",
                }}
              />
            </label>
            {prefixedError && (
              <div style={{ fontSize: "0.85rem", color: "#c0392b" }}>
                {prefixedError}
              </div>
            )}
            <button
              type="submit"
              style={{ display: "none" }}
              aria-hidden="true"
            />
          </form>
        )}
      </Modal>
    </>
  );
}

/**
 * Hero tile: prominent number, country flag + name, area label, and
 * the currently routed application. Designed to be the answer to
 * "what is this number, where is it from, where do calls go".
 */
function NumberHero({ detail }: { detail: NumberDetail }) {
  const area = areaLabel(detail);
  const formatted = formatPhoneNumber(
    detail.number,
    detail.countryCode,
    detail.areaCode,
  );
  return (
    <section
      className="tile"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "center",
        gap: 24,
        padding: 24,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 16, minWidth: 0 }}>
        <CountryFlag iso2={detail.countryIso2} title={detail.countryName} size={36} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: "1.6rem",
              fontWeight: 700,
              letterSpacing: "0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={formatted || detail.number}
          >
            {formatted || "—"}
          </div>
          <div
            style={{
              color: "var(--portal-muted)",
              fontSize: "0.95rem",
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={[detail.countryName, area].filter(Boolean).join(" · ")}
          >
            {[detail.countryName || "Unknown country", area]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Pill label={detail.numberType || "Number"} />
        {detail.active ? <PillSuccess label="Active" /> : <PillMuted label="Inactive" />}
      </div>
    </section>
  );
}

/**
 * Action tiles for jumping into the reports filtered on this number.
 * The reports themselves don't yet read the dnisId from the URL — the
 * link is forward-compatible so we can wire it up the day they do.
 */
function NumberQuickLinks({ detail }: { detail: NumberDetail }) {
  // The reports want the human-readable form ("+31 10 2005021") so the
  // subtitle matches what the hero shows above; we still pass the raw
  // dnisId on the URL so the report page itself can do the lookup.
  const formatted = formatPhoneNumber(
    detail.number,
    detail.countryCode,
    detail.areaCode,
  );
  const display = formatted || detail.number || "this number";
  return (
    <section
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: 12,
      }}
      aria-label="Quick links for this number"
    >
      <QuickLink
        to={`/reports/live/calls?dnisId=${detail.dnisId}`}
        icon={<PhoneCall size={18} />}
        title="Live calls"
        subtitle={`See current sessions on ${display}`}
      />
      <QuickLink
        to={`/reports/inbound?dnisId=${detail.dnisId}`}
        icon={<BarChart3 size={18} />}
        title="Inbound minutes"
        subtitle="Per-day inbound minutes for this number"
      />
    </section>
  );
}

function QuickLink({
  to,
  icon,
  title,
  subtitle,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <Link
      to={to}
      className="tile tile-link"
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 14,
        padding: 16,
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: "var(--portal-primary-soft)",
          color: "var(--portal-primary-darker)",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{title}</div>
        <div
          style={{
            color: "var(--portal-muted)",
            fontSize: "0.85rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {subtitle}
        </div>
      </div>
    </Link>
  );
}

/**
 * Secondary metadata tile: tariff, validity window, description, raw
 * dnis values etc. This is "the reference card" side of the screen for
 * customers who want to verify the technical details.
 */
function NumberMetadataTile({ detail }: { detail: NumberDetail }) {
  const items: { label: string; value: string }[] = [
    {
      label: "Current application",
      value: detail.application || "(no application)",
    },
    { label: "Type", value: detail.numberType || "—" },
    {
      label: "Country",
      value: [detail.countryName, detail.countryCode || detail.countryIso2.toUpperCase()]
        .filter(Boolean)
        .join(" · ") || "—",
    },
    { label: "Area", value: areaLabel(detail) || "—" },
    {
      label: "Active since",
      value: formatDate(detail.validFromUtc || detail.validTillUtc),
    },
    {
      label: "Active till",
      value: formatActiveTill(detail.validTillUtc),
    },
    {
      label: "Max duration",
      value: detail.maxDuration > 0 ? `${detail.maxDuration} sec` : "Unlimited",
    },
    {
      label: "Description",
      value: detail.description || "—",
    },
  ];

  return (
    <section className="tile" aria-label="Number metadata">
      <header className="tile-head" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Details</h3>
      </header>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "14px 24px",
          margin: 0,
        }}
      >
        {items.map((item) => (
          <div key={item.label}>
            <dt
              style={{
                color: "var(--portal-muted)",
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 2,
              }}
            >
              {item.label}
            </dt>
            <dd
              style={{
                margin: 0,
                color: "var(--portal-text)",
                fontSize: "0.95rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={item.value}
            >
              {item.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Pill({ label }: { label: string }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 999,
        border: "1px solid var(--portal-border)",
        background: "var(--portal-surface)",
        color: "var(--portal-text)",
        fontSize: "0.8rem",
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

function PillSuccess({ label }: { label: string }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 999,
        border: "1px solid var(--portal-status-success)",
        background: "var(--portal-status-success-soft)",
        color: "var(--portal-status-success-darker)",
        fontSize: "0.8rem",
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

function PillMuted({ label }: { label: string }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 999,
        border: "1px solid var(--portal-border)",
        background: "var(--portal-surface)",
        color: "var(--portal-muted)",
        fontSize: "0.8rem",
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

/**
 * "Application" picker tile. Renders as a dropdown for the four
 * well-known scriptTypeIds (Destination/Huntgroup/FlowBuilder/SipTrunk)
 * and locks itself to a read-only "Custom" pill when the row uses any
 * other scriptTypeId — that way customers can't accidentally override
 * a bespoke routing they don't fully understand from the SPA.
 *
 * Conceptually this is the aivrmanager equivalent of "what kind of
 * routing does this DNIS use?". Switching it to FlowBuilder reveals
 * the flow-schedule section below; switching to Destination/Huntgroup
 * will (later) reveal their dedicated link/management surfaces.
 */
function NumberApplicationTile({
  detail,
  busy,
  error,
  onChange,
}: {
  detail: NumberDetail;
  busy: boolean;
  error: string | null;
  onChange: (next: ScriptTypeId) => void;
}) {
  // The legacy spNumberListGet only sets allowEdit=1 when the row is a
  // FlowBuilder application (scriptTypeId = 2) — that flag governs the
  // *flow schedule* surface, not the script-type itself. For switching
  // between the four well-known scriptTypeIds the SPA only cares that
  // the row currently uses one of them; "Custom" rows stay locked so we
  // don't inadvertently overwrite a bespoke routing the SPA can't
  // re-create.
  const isCustom = !isKnownScriptType(detail.scriptTypeId);
  const canEdit = !isCustom && !busy;
  const helpText = isCustom
    ? `This number runs a custom application (${detail.scriptName || "unknown"}) and can only be changed by support.`
    : detail.scriptTypeId === SCRIPT_TYPE_IDS.flowBuilder
      ? "Use the FlowBuilder to create your application."
      : "Pick how incoming calls on this number should be routed.";

  return (
    <section className="tile" aria-labelledby="application-heading">
      <header className="tile-head" style={{ marginBottom: 8 }}>
        <h3 id="application-heading" style={{ margin: 0 }}>
          Application
        </h3>
      </header>
      <p
        style={{
          margin: "0 0 12px 0",
          color: "var(--portal-muted)",
          fontSize: "0.9rem",
        }}
      >
        {helpText}
      </p>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 16,
        }}
      >
        {isCustom ? (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 14px",
              borderRadius: 8,
              background: "var(--portal-surface)",
              border: "1px solid var(--portal-border)",
              fontWeight: 600,
              color: "var(--portal-text)",
              minWidth: 240,
            }}
            aria-readonly="true"
          >
            Custom
            {detail.scriptName && (
              <span
                style={{
                  fontWeight: 400,
                  color: "var(--portal-muted)",
                  fontSize: "0.85rem",
                }}
              >
                · {detail.scriptName}
              </span>
            )}
          </div>
        ) : (
          <select
            value={detail.scriptTypeId}
            disabled={!canEdit}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (
                isKnownScriptType(next) &&
                next !== detail.scriptTypeId
              ) {
                onChange(next);
              }
            }}
            aria-label="Application"
            style={{
              minWidth: 240,
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid var(--portal-border)",
              background: "white",
              fontSize: "0.95rem",
              fontFamily: "inherit",
              color: "var(--portal-text)",
              cursor: canEdit ? "pointer" : "not-allowed",
            }}
          >
            {SCRIPT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        )}
      </div>
      {error && (
        <div
          style={{
            marginTop: 12,
            color: "#c0392b",
            background: "#fdecea",
            border: "1px solid #e74c3c",
            borderRadius: 8,
            padding: 10,
            fontSize: "0.9rem",
          }}
        >
          {error}
        </div>
      )}
    </section>
  );
}

function NumberDestinationsTile({
  prefix,
  items,
  busy,
  onAdd,
  onOpen,
}: {
  prefix: string;
  items: NumberDestinationItem[];
  busy: boolean;
  onAdd: () => void;
  onOpen: (agentId: number) => void;
}) {
  const empty = items.length === 0;
  return (
    <section className="tile" aria-labelledby="destinations-heading">
      <header
        className="tile-head"
        style={{ display: "flex", alignItems: "center", gap: 12 }}
      >
        <h3 id="destinations-heading" style={{ margin: 0 }}>
          Destinations
        </h3>
        {!empty && (
          <span
            style={{
              color: "var(--portal-muted)",
              fontSize: "0.85rem",
            }}
          >
            {items.length} linked
          </span>
        )}
      </header>
      <p
        style={{
          margin: "0 0 12px 0",
          color: "var(--portal-muted)",
          fontSize: "0.9rem",
        }}
      >
        {empty
          ? "Add a destination — incoming calls on this number will route to the agent whose name starts with the dnis prefix."
          : "Calls route to the alphabetically first matching destination."}
      </p>
      {empty ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "12px 0",
          }}
        >
          <button
            type="button"
            className="btn btn-primary"
            onClick={onAdd}
            disabled={busy || !prefix}
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Plus size={16} /> Add destination
          </button>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "auto minmax(160px, max-content) auto 1fr auto",
            rowGap: 4,
            columnGap: 16,
            alignItems: "center",
          }}
        >
          {items.map((item) => (
            <DestinationRow key={item.agentId} item={item} onOpen={onOpen} />
          ))}
        </div>
      )}
    </section>
  );
}

function DestinationRow({
  item,
  onOpen,
}: {
  item: NumberDestinationItem;
  onOpen: (agentId: number) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item.agentId)}
      title={`Open ${item.displayName}`}
      style={{
        display: "contents",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        padding: 0,
      }}
      aria-label={`Open destination ${item.displayName}`}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "6px 0 6px 4px",
          color: "var(--portal-muted)",
        }}
      >
        <UserRound size={16} aria-hidden />
      </span>
      <span
        style={{
          padding: "6px 0",
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "left",
          justifySelf: "start",
        }}
      >
        {item.displayName || "Destination"}
      </span>
      <span style={{ padding: "6px 0" }}>
        <AgentStatusChip
          statusId={item.statusId}
          statusName={item.statusName}
          size="sm"
        />
      </span>
      <span
        style={{
          padding: "6px 0",
          color: "var(--portal-muted)",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: "0.85rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "right",
          justifySelf: "end",
        }}
      >
        {item.destination || "—"}
      </span>
      <span
        style={{
          padding: "6px 4px 6px 0",
          color: "var(--portal-muted)",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        <ChevronRight size={16} aria-hidden />
      </span>
    </button>
  );
}

/**
 * Mirror of <code>NumberDestinationsTile</code> for huntgroups. Renders
 * the matching huntgroups as clickable rows (icon | name | counts |
 * chevron) when the dnis prefix has matches; falls back to a centred
 * "Add huntgroup" CTA when there are none. Shape and layout intentionally
 * mirror the destinations tile so the surface stays consistent across
 * scriptType=Destination and scriptType=Huntgroup.
 */
function NumberHuntGroupsTile({
  prefix,
  items,
  busy,
  onAdd,
  onOpen,
}: {
  prefix: string;
  items: NumberHuntGroupItem[];
  busy: boolean;
  onAdd: () => void;
  onOpen: (huntGroupId: number) => void;
}) {
  const empty = items.length === 0;
  return (
    <section className="tile" aria-labelledby="huntgroups-heading">
      <header
        className="tile-head"
        style={{ display: "flex", alignItems: "center", gap: 12 }}
      >
        <h3 id="huntgroups-heading" style={{ margin: 0 }}>
          Huntgroups
        </h3>
        {!empty && (
          <span
            style={{
              color: "var(--portal-muted)",
              fontSize: "0.85rem",
            }}
          >
            {items.length} linked
          </span>
        )}
      </header>
      <p
        style={{
          margin: "0 0 12px 0",
          color: "var(--portal-muted)",
          fontSize: "0.9rem",
        }}
      >
        {empty
          ? "Add a huntgroup — incoming calls on this number will route to the huntgroup whose name starts with the dnis prefix."
          : "Calls route to the alphabetically first matching huntgroup."}
      </p>
      {empty ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "12px 0",
          }}
        >
          <button
            type="button"
            className="btn btn-primary"
            onClick={onAdd}
            disabled={busy || !prefix}
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Plus size={16} /> Add huntgroup
          </button>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "auto minmax(160px, max-content) auto 1fr auto",
            rowGap: 4,
            columnGap: 16,
            alignItems: "center",
          }}
        >
          {items.map((item) => (
            <HuntGroupRow
              key={item.huntGroupId}
              item={item}
              onOpen={onOpen}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function HuntGroupRow({
  item,
  onOpen,
}: {
  item: NumberHuntGroupItem;
  onOpen: (huntGroupId: number) => void;
}) {
  // X / Y agents online (kept as plain text instead of a chip — chips
  // reserve themselves for individual statuses; counts are just
  // metadata).
  const counts = `${item.assignedOnline} / ${item.assignedTotal} online`;
  return (
    <button
      type="button"
      onClick={() => onOpen(item.huntGroupId)}
      title={`Open ${item.displayName}`}
      style={{
        display: "contents",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        padding: 0,
      }}
      aria-label={`Open huntgroup ${item.displayName}`}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "6px 0 6px 4px",
          color: "var(--portal-muted)",
        }}
      >
        <Users size={16} aria-hidden />
      </span>
      <span
        style={{
          padding: "6px 0",
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "left",
          justifySelf: "start",
        }}
      >
        {item.displayName || "Huntgroup"}
      </span>
      <span
        style={{
          padding: "6px 0",
          color: "var(--portal-muted)",
          fontSize: "0.85rem",
        }}
      >
        {counts}
      </span>
      <span aria-hidden style={{ padding: "6px 0" }} />
      <span
        style={{
          padding: "6px 4px 6px 0",
          color: "var(--portal-muted)",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        <ChevronRight size={16} aria-hidden />
      </span>
    </button>
  );
}

function ScheduleTable({
  rows,
  onDelete,
  busy,
}: {
  rows: NumberScheduleItem[];
  onDelete: (applicationDnisId: number) => void;
  busy: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns:
          "minmax(0, 2fr) auto minmax(0, 1.2fr) auto",
        rowGap: 6,
        columnGap: 16,
        alignItems: "center",
      }}
    >
      <Header>Name</Header>
      <Header>State</Header>
      <Header>Valid from (UTC)</Header>
      <div />

      {rows.map((row) => (
        <ScheduleRow key={row.applicationDnisId} row={row} onDelete={onDelete} busy={busy} />
      ))}
    </div>
  );
}

function Header({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        color: "var(--portal-muted)",
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        paddingBottom: 6,
        borderBottom: "1px solid var(--portal-border)",
      }}
    >
      {children}
    </div>
  );
}

function ScheduleRow({
  row,
  onDelete,
  busy,
}: {
  row: NumberScheduleItem;
  onDelete: (applicationDnisId: number) => void;
  busy: boolean;
}) {
  const dim = row.stateName?.toLowerCase() === "expired";
  return (
    <>
      <div
        style={{
          fontWeight: 500,
          color: dim ? "var(--portal-muted)" : "var(--portal-text)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={row.name}
      >
        {row.name ? (
          <Link
            to={`/applications/${row.applicationId}`}
            style={{ color: "inherit", textDecoration: "none" }}
            title={`Open ${row.name}`}
          >
            {row.name}
          </Link>
        ) : (
          "—"
        )}
      </div>
      <ScheduleStateChip state={row.stateName} />
      <div
        style={{
          color: "var(--portal-muted)",
          fontSize: "0.9rem",
        }}
      >
        {formatDateTime(row.validFromUtc)}
      </div>
      <div style={{ textAlign: "right" }}>
        {row.allowDelete && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onDelete(row.applicationDnisId)}
            disabled={busy}
            title="Remove this scheduled assignment"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: "0.85rem",
            }}
          >
            <Trash2 size={14} /> Remove
          </button>
        )}
      </div>
    </>
  );
}

function ScheduleAppModal({
  onCancel,
  onConfirm,
  busy,
}: {
  onCancel: () => void;
  onConfirm: (applicationId: number, validFromUtc: string | null) => void;
  busy: boolean;
}) {
  const [apps, setApps] = useState<ApplicationListItem[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [applicationId, setApplicationId] = useState<number | "">("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  useEffect(() => {
    let active = true;
    applicationsApi
      .list()
      .then((res) => {
        if (!active) return;
        setApps(res.items ?? []);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setAppsError(
          err instanceof Error ? err.message : "Failed to load applications",
        );
      })
      .finally(() => {
        if (active) setAppsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const validFrom = useMemo(() => {
    if (!date) return null;
    // Combine date + time as a local-time ISO and let the SP receive it
    // as UTC; the legacy portal did the same (it sent local strings and
    // the SP interpreted them as UTC). Customers schedule in their own
    // timezone so we keep that behaviour.
    const t = time ? `${time}:00` : "00:00:00";
    return `${date}T${t}Z`;
  }, [date, time]);

  const canConfirm =
    !busy && !appsLoading && applicationId !== "" && Number(applicationId) > 0;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [busy, onCancel]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-modal-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        display: "grid",
        placeItems: "center",
        zIndex: 50,
      }}
    >
      <div
        className="tile"
        style={{ maxWidth: 480, width: "min(92vw, 480px)", padding: 24, gap: 16 }}
      >
        <h3 id="schedule-modal-title" style={{ margin: 0 }}>
          Schedule application
        </h3>

        {appsError && (
          <div
            style={{
              color: "#c0392b",
              background: "#fdecea",
              border: "1px solid #e74c3c",
              borderRadius: 8,
              padding: 10,
              fontSize: "0.9rem",
            }}
          >
            {appsError}
          </div>
        )}

        <Field label="Application">
          <select
            value={applicationId}
            onChange={(e) =>
              setApplicationId(e.target.value === "" ? "" : Number(e.target.value))
            }
            disabled={appsLoading}
            style={inputStyle}
          >
            <option value="">
              {appsLoading ? "Loading…" : "Select application"}
            </option>
            {apps.map((a) => (
              <option key={a.applicationId} value={a.applicationId}>
                {a.applicationName}
                {a.stateName ? ` · ${a.stateName}` : ""}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Date (optional, leave empty for now)">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={inputStyle}
          />
        </Field>

        <Field label="Time (optional, UTC)">
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            style={inputStyle}
          />
        </Field>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 4,
          }}
        >
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onConfirm(Number(applicationId), validFrom)}
            disabled={!canConfirm}
          >
            {busy ? "Scheduling…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          color: "var(--portal-muted)",
          fontSize: "0.8rem",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid var(--portal-border)",
  borderRadius: 6,
  fontSize: "0.95rem",
  fontFamily: "inherit",
  background: "white",
};

function areaLabel(d: { areaLabel?: string; areaCity?: string; areaState?: string; areaCode?: string }): string {
  // The API serves a pre-resolved label (vAivrmanagerNumber.areaLabel).
  // We still keep a tiny client-side fallback for the rare case that
  // an older response shape comes through (e.g. cached SW response).
  if (d.areaLabel) return d.areaLabel;
  const parts = [d.areaCity, d.areaState].filter(Boolean);
  if (parts.length > 0) return parts.join(", ");
  return d.areaCode || "";
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Same as <see cref="formatDate"/> but renders the smxSystems sentinel
 * "01-01-2100" (the default value of <c>Dnis.validTillUTC</c>, used to
 * mean "no end date scheduled") as a friendlier "No end date" label.
 *
 * The aivrmanager API serialises DateTime fields without a trailing "Z"
 * (e.g. "2100-01-01T00:00:00"), which the JS Date constructor would
 * otherwise interpret as *local* time — sliding the year off the 2100
 * boundary in non-UTC timezones and showing 01/01/2100 again. We force
 * UTC interpretation before doing the comparison so the sentinel is
 * matched everywhere.
 */
function formatActiveTill(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseAsUtc(iso);
  if (!d) return "—";
  if (d.getUTCFullYear() >= 2100) return "No end date";
  return formatDate(iso);
}

/** Parses an ISO-ish string as UTC even when no offset/Z is present. */
function parseAsUtc(iso: string): Date | null {
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  return isNaN(d.getTime()) ? null : d;
}
